import { apiClient } from 'src/shared/api/api.client';
import type {
  CityInfo,
  ClientAccessLevel,
  ClientCreate,
  ClientInfo,
  ClientSmallInfo,
  ClientUpdate,
  CountryInfo,
  DeveloperClassifierInfo,
  MyClosedDealsResponse,
  QueryLimitOrderParams,
  QueryLimitParams,
  QueryLimitParentParams,
  QueryParamsForDevelopers,
  RegionInfo,
} from 'src/logic/types/all.types';

export async function getClients(
  which: 'my' | 'received',
  params: QueryLimitParams,
): Promise<{
  clients?: ClientInfo[];
  error?: unknown;
}> {
  const { data, error } = await apiClient.GET(`/api/v1/clients/${which}`, {
    params: { query: { q: params.q, limit: params.limit, offset: params.offset } },
  });

  if (error) {
    return { error: error };
  }
  return { clients: data };
}

export async function getClosedDeals(params: QueryLimitOrderParams): Promise<{
  deals?: MyClosedDealsResponse;
  error?: unknown;
}> {
  const { data, error } = await apiClient.GET(`/api/v1/clients/my_closed_deals`, {
    params: {
      query: { q: params.q, limit: params.limit, offset: params.offset, order_by: params.order_by },
    },
  });

  if (error) {
    return { error: error };
  }
  return { deals: data };
}

export async function getClientById(clientId: string): Promise<{
  client?: ClientSmallInfo;
  error?: unknown;
}> {
  const { data, error } = await apiClient.GET('/api/v1/clients/{client_id}', {
    params: { path: { client_id: clientId } },
  });

  if (error) {
    return { error: error };
  }

  return { client: data };
}

export async function getClientFullInfoById(clientId: string): Promise<{
  client?: ClientInfo;
  error?: unknown;
}> {
  const { data, error } = await apiClient.GET('/api/v1/clients/{client_id}/full', {
    params: { path: { client_id: clientId } },
  });

  if (error) {
    return { error: error };
  }

  return { client: data };
}

export async function getClientAccessById(clientId: string): Promise<{
  accessLevel?: ClientAccessLevel;
  error?: unknown;
}> {
  const { data, error } = await apiClient.GET('/api/v1/clients/{client_id}/can_get_client', {
    params: { path: { client_id: clientId } },
  });

  if (error) {
    return { error: error };
  }

  return { accessLevel: data };
}

export async function createClient(dto: ClientCreate): Promise<{
  client?: ClientInfo;
  error?: unknown;
}> {
  const { data, error } = await apiClient.POST('/api/v1/clients', {
    body: dto,
  });

  if (error) {
    return { error: error };
  }

  return { client: data };
}

export async function updateClient(
  clientId: string,
  dto: ClientUpdate,
): Promise<{
  client?: ClientInfo;
  error?: unknown;
}> {
  const { data, error } = await apiClient.PATCH('/api/v1/clients/{client_id}', {
    params: { path: { client_id: clientId } },
    body: dto,
  });

  if (error) {
    return { error: error };
  }

  return { client: data };
}

export async function getCountries(params: QueryLimitParams): Promise<{
  countries?: CountryInfo[];
  error?: unknown;
}> {
  const { data, error } = await apiClient.GET('/api/v1/locations/countries', {
    params: { query: { q: params.q, limit: params.limit } },
  });

  if (error) {
    return { error: error };
  }

  return { countries: data };
}

export async function getRegions(params: QueryLimitParentParams): Promise<{
  regions?: RegionInfo[];
  error?: unknown;
}> {
  const { data, error } = await apiClient.GET('/api/v1/locations/regions', {
    params: { query: { q: params.q, limit: params.limit, parent_id: params.parentId } },
  });

  if (error) {
    return { error: error };
  }

  return { regions: data };
}

export async function getCities(params: QueryLimitParentParams): Promise<{
  cities?: CityInfo[];
  error?: unknown;
}> {
  const { data, error } = await apiClient.GET('/api/v1/locations/cities', {
    params: { query: { q: params.q, limit: params.limit, parent_id: params.parentId } },
  });

  if (error) {
    return { error: error };
  }

  return { cities: data };
}

export async function getDevelopers(params: QueryParamsForDevelopers): Promise<{
  developers?: DeveloperClassifierInfo[];
  error?: unknown;
}> {
  const { data, error } = await apiClient.GET('/api/v1/classifiers/developers', {
    params: { query: { q: params.q, limit: params.limit, country_id: params.country_id } },
  });

  if (error) {
    return { error: error };
  }

  return { developers: data };
}
